// frontend/src/pages/BetCopilotPage.js
// Web port of the mobile Bet Copilot (mobile/src/screens/BetCopilotScreen.js).
// Same backend contract: POST /api/assistant/chat -> { reply, proposal }.
// Keep this in step with the mobile screen.
import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, ArrowLeft } from 'lucide-react';
import { apiRequest, placeBet } from '../utils/settlementUtils';
import { formatMoney } from '../utils/money';

const SUGGESTIONS = [
  'Build me a safe 2-pick NBA slip for ₦100',
  'Draft a 3-pick Beast Mode under ₦200',
  'Who are good players to back tonight?',
];

const GREETING = {
  role: 'assistant',
  content:
    "Hi! I'm Playmaker. Tell me the sport, your budget, and how bold you're feeling — I'll put a slip together for you to review.",
};

// Mirrors the mobile pick label: goalscorer props read as score/no-goal,
// everything else as Over/Under <line> <stat>.
const pickDetail = (pick) => {
  if (/goalscorer/i.test(pick.statType || '')) {
    return pick.pickType === 1 ? '⚽ To Score Anytime' : '⚽ No Goal';
  }
  return `${pick.pickType === 1 ? 'Over' : 'Under'} ${pick.line} ${pick.statType}`;
};

const BetCopilotPage = ({ user, onBack }) => {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [balance, setBalance] = useState(user?.balance ?? 0);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const sendMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;
    if (!user?.id) {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Please log in to use Playmaker.' }]);
      return;
    }

    const next = [...messages, { role: 'user', content: trimmed }];
    setMessages(next);
    setInput('');
    setSending(true);

    try {
      // Backend only needs role+content; proposals stay local to the UI.
      const history = next.map(({ role, content }) => ({ role, content }));
      const result = await apiRequest('/assistant/chat', {
        method: 'POST',
        body: JSON.stringify({ userId: user.id, messages: history }),
      });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: result.reply, proposal: result.proposal },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: err.message || 'Something went wrong — please try again.' },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handlePlaceBet = async (proposal) => {
    if (!user?.id || placing) return;
    if (proposal.amount > balance) {
      window.alert('This stake exceeds your current balance.');
      return;
    }
    const ok = window.confirm(
      `Place ${formatMoney(proposal.amount)} ${proposal.type === 2 ? 'Beast Mode' : 'Safety Play'} — ` +
        `${proposal.picks.length} picks?\nPotential payout: ${formatMoney(proposal.potentialPayout)}`
    );
    if (!ok) return;

    setPlacing(true);
    try {
      await placeBet({
        userId: user.id,
        type: proposal.type,
        amount: proposal.amount,
        source: 2, // BetSource.Copilot - track AI-drafted bets
        picks: proposal.picks.map((p) => ({
          gameId: p.gameId,
          gameTime: p.gameTime,
          sportKey: p.sportKey,
          teamName: p.teamName,
          playerName: p.playerName,
          statType: p.statType,
          line: p.line,
          pickType: p.pickType,
          odds: p.odds,
        })),
      });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Bet placed — good luck! Want to build another slip?' },
      ]);
      // Refresh balance so the next slip's affordability check is accurate.
      try {
        const b = await apiRequest(`/users/${user.id}/balance`);
        if (b && b.balance != null) setBalance(b.balance);
      } catch {
        /* non-fatal */
      }
    } catch (err) {
      window.alert(err.message || 'Could not place the bet.');
    } finally {
      setPlacing(false);
    }
  };

  const ProposalCard = ({ proposal }) => (
    <div style={S.proposalCard}>
      <div style={S.proposalHeader}>
        <Sparkles size={16} color="#a78bfa" />
        <span style={S.proposalTitle}>
          {proposal.type === 2 ? 'Beast Mode' : 'Safety Play'} · {proposal.picks.length} picks
        </span>
      </div>

      {proposal.picks.map((pick, idx) => (
        <div key={idx} style={S.pickRow}>
          <div style={{ flex: 1 }}>
            <div style={S.pickPlayer}>{pick.playerName}</div>
            <div style={S.pickDetail}>{pickDetail(pick)}</div>
          </div>
          <span style={S.pickOdds}>{Number(pick.odds).toFixed(2)}</span>
        </div>
      ))}

      <div style={S.proposalFooter}>
        <div>
          <div style={S.stakeLabel}>Stake</div>
          <div style={S.stakeValue}>{formatMoney(proposal.amount)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={S.stakeLabel}>Potential Payout</div>
          <div style={S.payoutValue}>{formatMoney(proposal.potentialPayout)}</div>
        </div>
      </div>

      <button
        onClick={() => handlePlaceBet(proposal)}
        disabled={placing}
        style={{ ...S.placeBtn, opacity: placing ? 0.6 : 1 }}
      >
        {placing ? 'Placing…' : 'Review Done — Place Bet'}
      </button>
    </div>
  );

  return (
    <div style={S.container}>
      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn} aria-label="Back">
          <ArrowLeft size={20} color="white" />
        </button>
        <Sparkles size={20} color="#a78bfa" />
        <span style={S.headerTitle}>Playmaker</span>
      </div>

      <div ref={scrollRef} style={S.chat}>
        {messages.map((msg, idx) => (
          <div key={idx}>
            <div
              style={{
                ...S.bubble,
                ...(msg.role === 'user' ? S.userBubble : S.assistantBubble),
              }}
            >
              {msg.content}
            </div>
            {msg.proposal && <ProposalCard proposal={msg.proposal} />}
          </div>
        ))}

        {sending && (
          <div style={{ ...S.bubble, ...S.assistantBubble, ...S.typingBubble }}>
            <span style={S.spinner} />
            <span style={S.typingText}>Building your slip…</span>
          </div>
        )}

        {messages.length === 1 && !sending && (
          <div style={S.suggestions}>
            {SUGGESTIONS.map((s) => (
              <button key={s} style={S.suggestionChip} onClick={() => sendMessage(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={S.inputBar}>
        <input
          style={S.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage(input);
            }
          }}
          placeholder="Describe the bet you want…"
          maxLength={500}
          disabled={sending}
        />
        <button
          style={{ ...S.sendBtn, opacity: !input.trim() || sending ? 0.4 : 1 }}
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || sending}
          aria-label="Send"
        >
          <Send size={18} color="white" />
        </button>
      </div>

      <div style={S.disclaimer}>Suggestions are not guarantees. Bet responsibly · 18+</div>

      <style>{`@keyframes pbspin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const S = {
  container: {
    minHeight: '100vh',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#000',
    color: 'white',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    borderBottom: '1px solid #374151',
  },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, marginRight: 4 },
  headerTitle: { fontSize: 18, fontWeight: 600, color: 'white' },

  chat: { flex: 1, overflowY: 'auto', padding: 16, maxWidth: 720, width: '100%', margin: '0 auto', boxSizing: 'border-box' },

  bubble: {
    maxWidth: '85%',
    borderRadius: 16,
    padding: '10px 14px',
    marginBottom: 10,
    fontSize: 15,
    lineHeight: '21px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  userBubble: { marginLeft: 'auto', backgroundColor: '#2563eb', borderBottomRightRadius: 4 },
  assistantBubble: { marginRight: 'auto', backgroundColor: '#1f2937', borderBottomLeftRadius: 4 },

  typingBubble: { display: 'flex', alignItems: 'center', gap: 8 },
  typingText: { color: '#9ca3af', fontSize: 14 },
  spinner: {
    width: 14,
    height: 14,
    border: '2px solid #374151',
    borderTopColor: '#9ca3af',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'pbspin 0.8s linear infinite',
  },

  suggestions: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginTop: 8 },
  suggestionChip: {
    backgroundColor: 'rgba(124,58,237,0.15)',
    border: '1px solid #7c3aed',
    borderRadius: 16,
    padding: '8px 14px',
    color: '#c4b5fd',
    fontSize: 14,
    cursor: 'pointer',
    textAlign: 'left',
  },

  proposalCard: {
    backgroundColor: '#111827',
    border: '1px solid #7c3aed',
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  proposalHeader: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 },
  proposalTitle: { color: '#c4b5fd', fontWeight: 700, fontSize: 14 },
  pickRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
    borderTop: '1px solid #1f2937',
  },
  pickPlayer: { color: 'white', fontSize: 14, fontWeight: 600 },
  pickDetail: { color: '#9ca3af', fontSize: 12, marginTop: 2 },
  pickOdds: { color: '#10b981', fontSize: 14, fontWeight: 700 },
  proposalFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 10,
    paddingTop: 10,
    borderTop: '1px solid #1f2937',
  },
  stakeLabel: { color: '#6b7280', fontSize: 11 },
  stakeValue: { color: 'white', fontSize: 16, fontWeight: 700 },
  payoutValue: { color: '#10b981', fontSize: 16, fontWeight: 700 },
  placeBtn: {
    width: '100%',
    marginTop: 12,
    padding: '12px',
    background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
    color: 'white',
    fontSize: 15,
    fontWeight: 700,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },

  inputBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderTop: '1px solid #374151',
    maxWidth: 720,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  input: {
    flex: 1,
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: 20,
    padding: '10px 16px',
    color: 'white',
    fontSize: 15,
    outline: 'none',
  },
  sendBtn: {
    backgroundColor: '#2563eb',
    width: 40,
    height: 40,
    borderRadius: 20,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  disclaimer: { color: '#6b7280', fontSize: 11, textAlign: 'center', padding: '6px 0 10px' },
};

export default BetCopilotPage;
